import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import PandaViewSimplePreview from './panda-view-simple-previews/panda-view-simple-preview';
import FailedPhotoPreview from './failed-photo-preview';
import PhotoPreviewUnavailable from './photo-preview-unavailable';

/**
 * A container for the PandaView simple preview with orientation, wallet mode, and overlays
 *
 * @param {object} props
 * @param {React.ReactNode} [props.children] - Content to be rendered inside the container
 * @param {React.ReactNode} [props.topRightOverlay] - Content for the top right overlay
 * @param {React.ReactNode} [props.bottomRightOverlay] - Content for the bottom right overlay
 * @param {object} [props.raw_dimensions] - The raw dimensions of the image to determine orientation
 * @param {number} props.raw_dimensions.w - The width
 * @param {number} props.raw_dimensions.h - The height
 * @param {boolean} props.isWallet - Whether the preview is for a wallet print
 * @param {React.ReactElement} [props.placeholder] - When provided, renders this element in place of
 *                                                    `PandaViewSimplePreview`. Cloned once per slot
 *                                                    (twice for wallet prints). Use for loading states
 *                                                    such as a BlurhashCanvas before the thumbnail arrives.
 * @param {object} [props.pandaPreviewStyle] - Extra inline styles applied to the inner .panda-preview div
 * @param {Function} [props.onImageError] - Called when PandaView reports that its image could
 *                                          not be loaded, after this container has switched
 *                                          itself into the failed state. Lets a parent drop
 *                                          anything it is rendering over the preview.
 */
const PandaViewPreviewContainer = ({
  children,
  topRightOverlay,
  bottomRightOverlay,
  placeholder,
  pandaPreviewStyle,
  onImageError,
  ...props
}) => {
  const {
    raw_dimensions: rawDimensions,
    thumb_url: thumbUrl,
    isWallet,
    thumbnailFailed,
    filename,
    blurhash,
    fx,
    size,
  } = props;

  // PandaView loads its own image from thumb_url/large_url, and a broken image still
  // reports complete === true, so it reaches drawImage and throws. Once every URL has
  // failed the photo must never reach it.
  //
  // Both the flag and the detection live here rather than in PandaViewSimplePreviews
  // because callers such as duplicate detection render this container directly with a
  // photo spread into it, supplying no placeholder of their own. thumbnailFailed is also
  // client-only state that does not survive a reload, so a still-broken photo comes back
  // looking normal and has to be caught again at render time.
  const [loadFailed, setLoadFailed] = useState(false);
  useEffect(() => {
    setLoadFailed(false);
  }, [thumbUrl]);
  const handleImageError = useCallback(() => {
    setLoadFailed(true);
    if (onImageError) onImageError();
  }, [onImageError]);

  const hasFailed = !!thumbnailFailed || loadFailed;
  const resolvedPlaceholder =
    placeholder ??
    (hasFailed ? (
      <FailedPhotoPreview
        blurhash={blurhash}
        filename={filename}
        rawDimensions={rawDimensions}
        sizeArea={size?.area}
        fx={fx}
      />
    ) : null);

  return (
    <div
      className={classNames(
        'preview d-flex justify-content-center align-items-center m-auto',
        {
          landscape: rawDimensions && rawDimensions.h <= rawDimensions.w,
          portrait: rawDimensions && rawDimensions.h > rawDimensions.w,
          wallet: isWallet,
        },
      )}>
      <div
        className="panda-preview d-flex position-relative"
        style={pandaPreviewStyle}>
        {topRightOverlay && (
          <div className="panda-preview-overlay top-right">
            {topRightOverlay}
          </div>
        )}
        {(isWallet ? ['left', 'right'] : ['single']).map((key) =>
          resolvedPlaceholder ? (
            React.cloneElement(resolvedPlaceholder, { key })
          ) : (
            <PandaViewSimplePreview
              key={key}
              {...props}
              onImageError={handleImageError}
            />
          ),
        )}
        {children}
        {hasFailed && <PhotoPreviewUnavailable />}
        {bottomRightOverlay && (
          <div className="panda-preview-overlay bottom-right">
            {bottomRightOverlay}
          </div>
        )}
      </div>
    </div>
  );
};

PandaViewPreviewContainer.propTypes = {
  children: PropTypes.node,
  raw_dimensions: PropTypes.objectOf(PropTypes.number),
  isWallet: PropTypes.bool.isRequired,
  topRightOverlay: PropTypes.node,
  bottomRightOverlay: PropTypes.node,
  placeholder: PropTypes.element,
  onImageError: PropTypes.func,
  thumbnailFailed: PropTypes.bool,
  filename: PropTypes.string,
  thumb_url: PropTypes.string,
  blurhash: PropTypes.string,
  fx: PropTypes.string,
  size: PropTypes.shape({ area: PropTypes.arrayOf(PropTypes.number) }),
  pandaPreviewStyle: PropTypes.shape({
    width: PropTypes.string,
    height: PropTypes.string,
    overflow: PropTypes.string,
  }),
};

PandaViewPreviewContainer.defaultProps = {
  children: null,
  raw_dimensions: null,
  topRightOverlay: null,
  bottomRightOverlay: null,
  placeholder: null,
  onImageError: null,
  thumbnailFailed: false,
  filename: '',
  thumb_url: null,
  blurhash: null,
  fx: null,
  size: null,
  pandaPreviewStyle: undefined,
};

export { PandaViewPreviewContainer };
